import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Upload, Button, message, Select, Spin, Result } from 'antd';
import { useTranslation } from 'react-i18next';
import submitIcon from '../assets/icons/submit.svg';
import applicationIcon from "../assets/icons/application.png";
import { LoadingOutlined } from '@ant-design/icons';
import axiosInstance from '../services/axios';

const ApplicationFormModal = ({ open, onClose, title, formConfig, amount, formType = 'application', preloadedFiles = {} }) => {
  const { i18n } = useTranslation();
  const [form] = Form.useForm();
  const [fileListMap, setFileListMap] = useState({}); // Map of fieldName -> fileList
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success' | 'failed' | null
  const [formSubmissionData, setFormSubmissionData] = useState(null); // Store form submission for payment linking
  
  // Use formSubmissionData to avoid ESLint warning
  console.log('Current form submission:', formSubmissionData);

  // If parent provides preloaded upload responses, populate fileListMap so validators accept them
  useEffect(() => {
    if (open && preloadedFiles && Object.keys(preloadedFiles).length) {
      const mapped = {};
      Object.keys(preloadedFiles).forEach((fieldName) => {
        const resp = preloadedFiles[fieldName];
        let name = resp.name;
        if (!name && resp.file_url) {
          const parts = resp.file_url.split('/');
          name = parts[parts.length - 1];
        }

        mapped[fieldName] = [{
          uid: resp.upload_uid || `pre_${fieldName}`,
          name: name || 'uploaded_file',
          size: resp.size || 0,
          type: resp.type || (name && name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : ''),
          response: resp,
          status: 'done'
        }];
      });

      setFileListMap(prev => ({ ...prev, ...mapped }));
    }
  }, [open, preloadedFiles]);

  // ✅ Razorpay Payment Flow
  const handlePayment = async (formSubmissionData) => {
    // amount may be like "₹50" or "50" - try to parse robustly
    const numericAmount = (() => {
      if (!amount && amount !== 0) return NaN;
      if (typeof amount === 'number') return amount;
      // remove non-digit and non-dot characters
      const cleaned = String(amount).replace(/[^\d.]/g, '');
      return Number(cleaned);
    })();

    if (!numericAmount || isNaN(numericAmount)) {
      message.error(
        i18n.language === 'mr'
          ? "कृपया वैध रक्कम प्रदान करा"
          : "Please provide a valid amount in props."
      );
      return;
    }

    try {
      setLoading(true);

      // 1️⃣ Create order on Django backend
      const amountInRupees = numericAmount;
      const { data } = await axiosInstance.post("/create-order/", {
        amount: amountInRupees, // Amount in INR (backend converts to paise)
        clientId:1,
        form_submission_id: formSubmissionData?.id || null,
      });

      // 2️⃣ Razorpay Checkout options
      const options = {
        key: data.key, // Razorpay Key ID from backend
        amount: data.amount, // Amount in paise
        currency: data.currency,
        name: "Municipal Application Payment",
        description: `${title} - Online Payment`,
        order_id: data.order_id,


        handler: async function (response) {
          console.log("Razorpay Response:", response);
          try {
            // 3️⃣ Verify payment with backend
            const verifyRes = await axiosInstance.post("/verify-payment/", {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              // optionally include form submission id to link payment:
              form_submission_id: formSubmissionData.id || null,
              clientId:1
            });

            if (verifyRes.data.status === "success") {
              setPaymentStatus('success');
              message.success(
                i18n.language === 'mr'
                  ? "पेमेंट यशस्वी झाले!"
                  : "Payment successful!"
              );
            } else {
              setPaymentStatus('failed');
              message.error(
                i18n.language === 'mr'
                  ? "पेमेंट सत्यापन अयशस्वी!"
                  : "Payment verification failed!"
              );
            }
          } catch (err) {
            console.error("Verification Error:", err);
            setPaymentStatus('failed');
            message.error(
              i18n.language === 'mr'
                ? "पेमेंट सत्यापित करताना त्रुटी"
                : "Error verifying payment."
            );
          } finally {
            setLoading(false);
          }
        },

        theme: { color: "#3BB54A" },
        modal: {
          ondismiss: () => {
            setLoading(false);
            message.info(
              i18n.language === 'mr'
                ? "पेमेंट विंडो बंद केली गेली"
                : "Payment window closed."
            );
          }
        }
      };

      // Load Razorpay script if not already loaded
      if (!window.Razorpay) {
        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => {
          const rzp = new window.Razorpay(options);
          rzp.open();
        };
        document.head.appendChild(script);
      } else {
        const rzp = new window.Razorpay(options);
        rzp.open();
      }
    } catch (error) {
      console.error("Error creating order:", error);
      message.error(
        i18n.language === 'mr'
          ? "ऑर्डर तयार करताना त्रुटी. कृपया पुन्हा प्रयत्न करा."
          : "Error creating order. Please try again."
      );
      setLoading(false);
    }
  };

  // 🧾 Handle Form Submission
  const onFinish = async (values) => {
    console.log('Form values:', values);

    try {
      setLoading(true);

      // Validate all required files are uploaded (ensure backend returned success:true)
      const requiredFileFields = formConfig.filter(f => f.type === 'file' && f.required);
      for (const field of requiredFileFields) {
        const fileList = fileListMap[field.name];
        console.log("check form submit  ", fileList);
        if (!fileList || !fileList.length || !fileList[0].response || fileList[0].response.success !== true) {
          throw new Error(
            i18n.language === 'mr'
              ? `${field.labelMr} अपलोड करण्यात विफल`
              : `${field.labelEn} upload failed`
          );
        }
      }

      // Build form data object with uploaded file responses
      const formDataJson = {
        form_type: formType,
        form_data: {},
        files: {}
      };

      // Add all form field values (excluding file fields)
      Object.keys(values).forEach(key => {
        const field = formConfig.find(f => f.name === key);
        if (field && field.type === 'file') {
          return; // Skip file fields
        }
        formDataJson.form_data[key] = values[key];
      });

      // Add uploaded file responses
      Object.keys(fileListMap).forEach(fieldName => {
        const fileList = fileListMap[fieldName];
        if (fileList && fileList.length > 0 && fileList[0].response && fileList[0].response.success) {
          formDataJson.files[fieldName] = {
            name: fileList[0].name,
            size: fileList[0].size,
            type: fileList[0].type,
            uploadResponse: fileList[0].response
          };
        }
      });

      // Add clientId
      formDataJson.form_data.clientId = localStorage.getItem('clientId') || '1';

      console.log('Submitting form data:', formDataJson);

      // Submit form data to backend
      const response = await axiosInstance.post('/form-submissions/', {...formDataJson,clientId:1});

      console.log('Form submission successful:', response.data);
      setFormSubmissionData(response.data);

      message.success(
        i18n.language === 'mr'
          ? 'अर्ज यशस्वीरित्या सबमिट केला!'
          : 'Application submitted successfully!'
      );

      // Proceed to payment if amount is provided
      if (amount) {
        // keep loading true until payment flow handles it
        handlePayment(response.data);
      } else {
        setPaymentStatus('success');
        setLoading(false);
      }

    } catch (submissionError) {
      console.error('Form submission error:', submissionError);
      message.error(
        submissionError.message || (i18n.language === 'mr'
          ? 'अर्ज सबमिट करताना त्रुटी'
          : 'Error submitting application. Please try again.')
      );
      setLoading(false);
    }
  };

  // 📁 File Upload - Handle multiple files per field
  const getUploadProps = (fieldName) => ({
    beforeUpload: async (file) => {
      const isValidSize = file.size / 1024 / 1024 < 5; // 5MB limit
      const validFileTypes = [
        'application/pdf',
        'image/jpeg',
        'image/png',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];

      // Some browsers may not populate file.type for .doc/.docx, so also check extension
      const filename = (file.name || '').toLowerCase();
      const extAllowed = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];
      const hasAllowedExt = extAllowed.some(ext => filename.endsWith(ext));

      const isValidType = validFileTypes.includes(file.type) || hasAllowedExt;

      if (!isValidSize) {
        message.error(
          i18n.language === 'mr'
            ? 'फाइल 5MB पेक्षा कमी असावी!'
            : 'File must be smaller than 5MB!'
        );
        return Upload.LIST_IGNORE;
      }

      if (!isValidType) {
        message.error(
          i18n.language === 'mr'
            ? 'केवळ PDF, JPG, PNG किंवा DOC फाइलें अनुमत आहेत!'
            : 'Only PDF, JPG, PNG, or DOC files are allowed!'
        );
        return Upload.LIST_IGNORE;
      }

      // Upload file to backend with proper FormData handling
      try {
        const formData = new FormData();
        formData.append('file', file); // Append actual File object
        formData.append('clientId', localStorage.getItem('clientId') || '1');
        formData.append('fieldName', fieldName);

        const res = await axiosInstance.post('/upload-file/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          }
        });

        // Check for successful upload
        if (res.status === 200 && res.data) {
          message.success(i18n.language === 'mr' ? 'फाइल यशस्वीरित्या अपलोड झाली!' : 'File uploaded successfully!');

          // Store backend response with file metadata
          setFileListMap(prev => ({
            ...prev,
            [fieldName]: [{
              uid: file.uid,
              name: file.name,
              size: file.size,
              type: file.type,
              response: res.data,
              status: 'done'
            }]
          }));
        } else {
          throw new Error(res.data?.error || 'Upload failed');
        }
      } catch (err) {
        console.error('File upload error:', err);
        const errorMsg = err.response?.data?.error || err.message || 'Error uploading file';
        message.error(
          i18n.language === 'mr'
            ? `फाइल अपलोड करताना त्रुटी: ${errorMsg}`
            : `Error uploading file: ${errorMsg}`
        );
      }
      // Prevent Antd Upload from auto-adding the file to its internal list (we manage fileListMap ourselves)
      return Upload.LIST_IGNORE;
    },
    fileList: fileListMap[fieldName] || [],
    onChange: ({ fileList: newFileList }) => {
      // We manage fileListMap explicitly on successful upload / remove.
      // This handler left intentionally minimal.
    },
    onRemove: (file) => {
      // Remove file mapping when user removes
      setFileListMap(prev => {
        const copy = { ...prev };
        copy[fieldName] = [];
        return copy;
      });
      return true; // allow remove
    },
    showUploadList: true,
    accept: ".pdf,.jpg,.jpeg,.png,.doc,.docx"
  });

  // 🧩 Dynamic Form Renderer
  const renderFormItems = () => {
    return formConfig.map((field) => {
      switch (field.type) {
        case 'file':
          return (
            <Form.Item
              key={field.name}
              name={field.name}
              label={i18n.language === 'mr' ? field.labelMr : field.labelEn}
              // rely on custom validator below (remove default required to avoid race with Upload)
              rules={[
                {
                  validator: () => {
                    const fieldFileList = fileListMap[field.name] || [];
                    // If field is required and there's no uploaded file with success -> reject
                    const hasValidFile = fieldFileList.length > 0 && fieldFileList[0].response && fieldFileList[0].response.success;
                    if (field.required && !hasValidFile) {
                      return Promise.reject(new Error(
                        i18n.language === 'mr' ? (field.messageMr || 'कृपया फाइल निवडा आणि अपलोड करा') : (field.messageEn || 'Please select and upload a file')
                      ));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Upload {...getUploadProps(field.name)} maxCount={1}>
                <Button>
                  <span className="choose-file-btn">
                    {i18n.language === 'mr' ? 'फाइल निवडा' : 'Choose File'}
                  </span>
                  <span className="file-status" style={{ marginLeft: 8 }}>
                    {(fileListMap[field.name]?.[0]?.name) || (i18n.language === 'mr' ? 'कोणतीही फाइल निवडलेली नाही' : 'No File Chosen')}
                  </span>
                </Button>
              </Upload>
            </Form.Item>
          );

        case 'select':
          return (
            <Form.Item
              key={field.name}
              name={field.name}
              label={i18n.language === 'mr' ? field.labelMr : field.labelEn}
              rules={[
                {
                  required: field.required,
                  message: i18n.language === 'mr' ? field.messageMr : field.messageEn
                }
              ]}
            >
              <Select placeholder={i18n.language === 'mr' ? '-- निवडा --' : '-- Select --'}>
                {field.options?.map((option) => (
                  <Select.Option
                    key={option.valueEn}
                    value={option.valueEn}
                  >
                    {i18n.language === 'mr' ? option.labelMr : option.labelEn}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          );

        case 'textarea':
          return (
            <Form.Item
              key={field.name}
              name={field.name}
              label={i18n.language === 'mr' ? field.labelMr : field.labelEn}
              rules={[
                {
                  required: field.required,
                  message: i18n.language === 'mr' ? field.messageMr : field.messageEn
                },
                {
                  min: 10,
                  message: i18n.language === 'mr' ? 'कमीतकमी 10 वर्ण प्रविष्ट करा' : 'Please enter at least 10 characters'
                },
                {
                  max: 500,
                  message: i18n.language === 'mr' ? '500 वर्णांपेक्षा कमी असणे आवश्यक आहे' : 'Maximum 500 characters allowed'
                },
                {
                  whitespace: true,
                  message: i18n.language === 'mr' ? 'फक्त रिकामी जागा प्रविष्ट करू शकत नाही' : 'Cannot enter only spaces'
                }
              ]}
            >
              <Input.TextArea
                rows={4}
                placeholder={i18n.language === 'mr' ? field.placeholderMr : field.placeholderEn}
                maxLength={500}
                showCount
              />
            </Form.Item>
          );

        case 'email':
          return (
            <Form.Item
              key={field.name}
              name={field.name}
              label={i18n.language === 'mr' ? field.labelMr : field.labelEn}
              rules={[
                {
                  required: field.required,
                  message: i18n.language === 'mr' ? field.messageMr : field.messageEn
                },
                {
                  type: 'email',
                  message: i18n.language === 'mr' ? 'वैध ईमेल प्रविष्ट करा' : 'Please enter a valid email'
                },
                {
                  whitespace: true,
                  message: i18n.language === 'mr' ? 'फक्त रिकामी जागा प्रविष्ट करू शकत नाही' : 'Cannot enter only spaces'
                }
              ]}
            >
              <Input type="email" />
            </Form.Item>
          );

        case 'tel':
          return (
            <Form.Item
              key={field.name}
              name={field.name}
              label={i18n.language === 'mr' ? field.labelMr : field.labelEn}
              rules={[
                {
                  required: field.required,
                  message: i18n.language === 'mr' ? field.messageMr : field.messageEn
                },
                {
                  pattern: /^[0-9]{10}$/,
                  message: i18n.language === 'mr' ? '10 अंकों का फोन नंबर दर्ज करें' : 'Please enter a 10-digit phone number'
                },
                {
                  whitespace: true,
                  message: i18n.language === 'mr' ? 'फक्त रिकामी जागा प्रविष्ट करू शकत नाही' : 'Cannot enter only spaces'
                }
              ]}
            >
              <Input
                type="tel"
                placeholder={i18n.language === 'mr' ? '10 अंकों का मोबाइल नंबर' : 'Enter 10-digit mobile number'}
              />
            </Form.Item>
          );

        case 'date':
          return (
            <Form.Item
              key={field.name}
              name={field.name}
              label={i18n.language === 'mr' ? field.labelMr : field.labelEn}
              rules={[
                {
                  required: field.required,
                  message: i18n.language === 'mr' ? field.messageMr : field.messageEn
                },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const selectedDate = new Date(value);
                    const today = new Date();
                    // Compare dates ignoring time
                    selectedDate.setHours(0, 0, 0, 0);
                    today.setHours(0, 0, 0, 0);
                    if (selectedDate > today) {
                      return Promise.reject(new Error(
                        i18n.language === 'mr' ? 'भविष्यतची तारीख निवडू शकत नाही' : 'Cannot select a future date'
                      ));
                    }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Input type="date" />
            </Form.Item>
          );

        default: // text input
          return (
            <Form.Item
              key={field.name}
              name={field.name}
              label={i18n.language === 'mr' ? field.labelMr : field.labelEn}
              rules={[
                {
                  required: field.required,
                  message: i18n.language === 'mr' ? field.messageMr : field.messageEn
                },
                {
                  min: 2,
                  message: i18n.language === 'mr' ? 'कमीतकमी 2 वर्ण प्रविष्ट करा' : 'Please enter at least 2 characters'
                },
                {
                  max: 100,
                  message: i18n.language === 'mr' ? '100 वर्णांपेक्षा कमी असणे आवश्यक आहे' : 'Maximum 100 characters allowed'
                },
                {
                  whitespace: true,
                  message: i18n.language === 'mr' ? 'फक्त रिकामी जागा प्रविष्ट करू शकत नाही' : 'Cannot enter only spaces'
                }
              ]}
            >
              <Input
                type="text"
                placeholder={i18n.language === 'mr' ? field.placeholderMr : field.placeholderEn}
              />
            </Form.Item>
          );
      }
    });
  };

  // 🎉 Success or Failure Result
  const renderResult = () => {
    if (paymentStatus === 'success') {
      return (
        <Result
          status="success"
          title={i18n.language === 'mr' ? 'यशस्वी!' : 'Success!'}
          subTitle={
            amount
              ? (i18n.language === 'mr'
                ? 'आपला व्यवहार यशस्वीरित्या पूर्ण झाला.'
                : 'Your transaction was completed successfully.')
              : (i18n.language === 'mr'
                ? 'आपला अर्ज यशस्वीरित्या सबमिट केला गेला.'
                : 'Your application was submitted successfully.')
          }
          extra={[
            <Button
              type="primary"
              key="done"
              onClick={() => {
                setPaymentStatus(null);
                form.resetFields();
                setFileListMap({});
                setFormSubmissionData(null);
                onClose();
              }}
            >
              {i18n.language === 'mr' ? 'पूर्ण' : 'Done'}
            </Button>,
          ]}
        />
      );
    }

    if (paymentStatus === 'failed') {
      return (
        <Result
          status="error"
          title={i18n.language === 'mr' ? 'अयशस्वी' : 'Failed'}
          subTitle={i18n.language === 'mr'
            ? 'आपल्या पेमेंटमध्ये काहीतरी चूक झाली.'
            : 'Something went wrong while processing your payment.'}
          extra={[
            <Button
              key="retry"
              type="primary"
              onClick={() => {
                setPaymentStatus(null);
                handlePayment();
              }}
            >
              {i18n.language === 'mr' ? 'पुन्हा प्रयत्न करा' : 'Retry'}
            </Button>,
            <Button
              key="close"
              onClick={() => {
                setPaymentStatus(null);
                onClose();
              }}
            >
              {i18n.language === 'mr' ? 'बंद करा' : 'Close'}
            </Button>,
          ]}
        />
      );
    }

    return null;
  };

  return (
    <Modal
      open={open}
      onCancel={() => {
        onClose();
        form.resetFields();
        setFileListMap({});
        setPaymentStatus(null);
      }}
      title={
        <h2 className='flex gap-2'>
          <img loading="lazy" className="w-8 h-8 relative bottom-1" src={applicationIcon} alt="application Icon" />
          {title}
        </h2>
      }
      width={800}
      footer={null}
      className="application-form-modal"
    >
      <Spin
        spinning={loading}
        indicator={<LoadingOutlined style={{ fontSize: 36, color: '#3BB54A' }} spin />}
        tip={loading && !paymentStatus ? (
          i18n.language === 'mr'
            ? "कृपया प्रतीक्षा करा..."
            : "Processing, please wait..."
        ) : null}
      >
        {!paymentStatus ? (
          <>
            <p>
              {i18n.language === 'mr'
                ? 'कृपया तक्रार किंवा सल्ला सादर करण्यासाठी खालील फॉर्म भरा. तुमच्या समस्या किंवा सूचना आम्हाला महत्त्वाच्या आहेत.'
                : 'Please fill out the form below to submit your application. Your information is important to us.'}
            </p>

            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
              className="application-form"
            >
              {renderFormItems()}
              <Form.Item className="form-actions">
                <Button
                  type="primary"
                  htmlType="submit"
                  className="submit-btn"
                  loading={loading}
                  disabled={loading}
                >
                  <img loading="lazy" src={submitIcon} alt="Submit" className="w-2 h-2 filter brightness-0 invert" />
                  {i18n.language === 'mr' ? 'सबमिट करा' : 'Submit'}
                </Button>
              </Form.Item>
            </Form>
          </>
        ) : (
          renderResult()
        )}
      </Spin>
    </Modal>
  );
};

export default ApplicationFormModal;
